'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Mail } from 'lucide-react';
import { Button, Input, toast } from '@stenos/ui';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';

export function LoginForm() {
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect_to') ?? '/';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState<'google' | 'github' | 'magic' | null>(null);

  const supabase = createSupabaseBrowserClient();
  const callbackUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback?next=${encodeURIComponent(redirectTo)}`;

  async function loginGoogle() {
    setLoading('google');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: callbackUrl },
    });
    if (error) {
      toast.error('Erreur Google : ' + error.message);
      setLoading(null);
    }
  }

  async function loginGitHub() {
    setLoading('github');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: callbackUrl },
    });
    if (error) {
      toast.error('Erreur GitHub : ' + error.message);
      setLoading(null);
    }
  }

  async function loginMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading('magic');
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: callbackUrl },
    });
    setLoading(null);
    if (error) {
      toast.error('Erreur : ' + error.message);
    } else {
      toast.success('Email envoyé', {
        description: 'Vérifie ta boîte (' + email + '), un lien de connexion vient d\'arriver.',
      });
      setEmail('');
    }
  }

  return (
    <div className="space-y-3">
      <Button
        variant="secondary"
        size="lg"
        loading={loading === 'google'}
        onClick={loginGoogle}
        className="w-full justify-center"
        leftIcon={<GoogleIcon />}
      >
        Continuer avec Google
      </Button>
      <Button
        variant="secondary"
        size="lg"
        loading={loading === 'github'}
        onClick={loginGitHub}
        className="w-full justify-center"
        leftIcon={<GitHubIcon />}
      >
        Continuer avec GitHub
      </Button>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--color-border-subtle)]" />
        <span className="text-xs uppercase tracking-wider text-[var(--color-fg-tertiary)]">ou</span>
        <div className="h-px flex-1 bg-[var(--color-border-subtle)]" />
      </div>

      <form onSubmit={loginMagicLink} className="space-y-3">
        <Input
          type="email"
          placeholder="ton@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          aria-label="Adresse email"
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading === 'magic'}
          className="w-full justify-center"
          leftIcon={<Mail className="h-4 w-4" />}
        >
          Recevoir un lien de connexion
        </Button>
      </form>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.28-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.85 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.35-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.67-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.67 2.84C6.72 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.55v-1.93c-3.2.7-3.87-1.54-3.87-1.54-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.69 1.25 3.34.96.1-.74.4-1.25.72-1.54-2.55-.29-5.24-1.27-5.24-5.65 0-1.25.45-2.27 1.18-3.07-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.16 1.18a10.95 10.95 0 0 1 5.74 0c2.2-1.49 3.16-1.18 3.16-1.18.62 1.58.23 2.75.11 3.04.74.8 1.18 1.82 1.18 3.07 0 4.39-2.69 5.36-5.25 5.65.41.36.78 1.06.78 2.13v3.16c0 .31.21.67.8.55C20.21 21.39 23.5 17.07 23.5 12 23.5 5.65 18.35.5 12 .5z" />
    </svg>
  );
}
